import {
  WORN_SLOTS,
  type WornSlot,
  BUILDABLE_TYPES,
  BUILD_TIME_MS,
  DEMOLISH_REFUND_BP,
  DEMOLISH_UNDO_MS,
  DISTRICT_KINDS,
  ELIXIRS,
  ELIXIR_KINDS,
  ENCHANTS,
  FORGE,
  HERO_CLASSES,
  LAB,
  LEVEL4_IRON,
  LEVEL5_IRON,
  LEVEL6_IRON,
  LAND_ERAS,
  EARTH_IDS,
  BRANCHES,
  BRANCH_COST_BP,
  BRANCH_TIME_HOURS,
  MOUNTS,
  MOUNT_ERA,
  MOUNT_LEVEL,
  LAND_LEVELS,
  LEVEL_COST_BP,
  MATERIAL3_IRON_PER_LEVEL,
  MATERIAL_UNLOCK_LEVEL,
  MATERIAL_UPGRADE_TIME_BP,
  MAX_ENCHANTS,
  MERCENARY,
  RESPEC,
  CARRY_MODES,
  LOADOUTS,
  ROSTER,
  SALVAGE_DUST,
  SALVAGE_PLUS_BP,
  SKILL_NODES,
  SKILL_TREES,
  STRUCTURES,
  TEMPER,
  CARVE,
  AILMENT_CURE,
  ATTUNE,
  GEMS,
  OFFERINGS,
  OFFERING_KINDS,
  PALADIN_AURAS,
  RELIC_AWAKEN,
  RES_ATTRS,
  RES_TYPE,
  SOCKET_OPEN,
  DIRECTED_REFORGE,
  CAT,
  ENSIGN,
  WAVES_PER_MAREA,
  bossAt,
  MARKET,
  RECIPE_SABER,
  RECIPES,
  REFORGE,
  RITE_IDS,
  SABER_STRUCTURES,
  GEAR_BASES,
  canWearBase,
  MASTERWORK,
  GEM_CUTS,
  GEM_CUT_COST,
  GEM_CUT_LEVEL,
  GEM_FUSE,
  type GemCut,
  XP,
  hoursMs,
  isResAttr,
  res,
  type GearAttr,
  type Resources,
  type StructLevel,
  type StructureType,
  LAND_ONLY_TYPES,
  CANCEL_GRACE_MS,
} from '@somnhold/content';
import { imax, imin, mulBp } from '../math/fixed.ts';
import { rngInt } from '../math/rng.ts';
import { DIRS, hexKey, parseHexKey } from '../hex.ts';
import { isBig } from '../land.ts';
import { anchorOf, clearParts, isPart, placeFail, placeParts } from './footprint.ts';
import {
  addRes,
  baseCost,
  builtMaxHp,
  event,
  findPlayer,
  hasRes,
  isBuildableCell,
  isGateCell,
  isNonNegRes,
  scaleRes,
  sumRes,
} from './core.ts';
import { PANOPLY, UNIQUES, type UniqueSlot } from '@somnhold/content';
import { syncSlot } from './panoply.ts';
import { infuse, infuseBlock, rearm, scrapCap, scrapsFall, scrapsPay, uninfuse } from './scraps.ts';
import { giveItem, recipePiece, wornItems, awakenBlock, awakenItem, effectPool, rollRange, equipItem, findItem, preparedGear, forgeCost, forgedOf, forgeMaxPlus, gemKey, itemSockets, mainAttr, MAX_BAG, nextAwaken, pouch, reforgeChoices, reforgeCost, reforgeItem, sealSpent, socketMax, temperItem } from './items.ts';
import { calendarMarea, offeringsFor } from './resolve.ts';
import { boulderSlope, catNamer, knowsSaber, propsHold, redraftForRite, riteFor, riteOpen, saberLocked } from './saberes.ts';
import { carvedGlyphs, chosenForms, grantXp, knownSkills, knownVariants, pointsSpent, workProgressMs } from './progress.ts';
import { DEFAULT_PROFILE } from '../battle/create.ts';
import { keptGolems, advanceTime, completeJob, golemsFor } from './time.ts';
import { GREAT_WORK_KEY, activeArtificer, bestLevel, costAt, countOf, eraOf, greatWorkCost, groupBelow, levelEraLocked, levelTop, minEraOf, nextDistrict, standing, towerCap } from './eras.ts';
import { withDistrict } from '../walls.ts';
import { repairQuote } from './repair.ts';
import { creditGiven, creditStorage, creditWork } from './deeds.ts';
import { applySagaChoice, applySagaRerun, applySagaRerunNext, applySagaVote, settleSagaVote } from './saga.ts';
import { applySideChoice, applySideGo, settleSideVotes } from './sides.ts';
import type { CampaignCommand, CampaignState, CellState, CommandResult, ItemInstance, Job, Loadout, Ms } from './types.ts';
import { earthFail, earthJob } from './earth.ts';

const fail = (reason: string): CommandResult => ({ ok: false, reason });

/** Dust for one offering at the keep this Marea (it grows with the Marea). */
export const offeringCost = (s: CampaignState): number => OFFERINGS.dust * (s.mareaIndex + 1);

/** Dust for a forge recipe's piece (docs/SABERES.md §4.4): an uncommon's reforge, times RECIPES.dustX. */
export const recipeDust = (_s: CampaignState): number => REFORGE.dust.uncommon * RECIPES.dustX;
const OK: CommandResult = { ok: true };

/** Material upgrade (wood → stone) costs 40 % of the level's cost, paid in stone. */
const materialCost = (base: Resources, level: StructLevel): Resources => {
  const levelCost = scaleRes(base, LEVEL_COST_BP[level - 1] as number);
  return res({ stone: mulBp(levelCost.wood + levelCost.stone + levelCost.crystal, 4000) });
};

/** A cost with some iron on top (level 4, iron cladding). */
const withIron = (c: Resources, iron: number): Resources => (iron > 0 ? { ...c, iron: (c.iron ?? 0) + iron } : c);

/** What raising the structure on (q, r) to `next` costs; the bastion screen shows the same numbers. A military district discounts the iron too. */
export const levelUpCost = (s: CampaignState, q: number, r: number, structure: StructureType, next: StructLevel): Resources => {
  const c = costAt(s, q, r, withIron(scaleRes(baseCost(s, structure), LEVEL_COST_BP[next - 1] as number), next === 6 ? LEVEL6_IRON : next === 5 ? LEVEL5_IRON : next === 4 ? LEVEL4_IRON : 0));
  // Terraced land: levels 3 and up without an artificer playing in the group cost more (docs/OBRAS.md §9).
  return s.map.land && next >= 3 && !activeArtificer(s) ? scaleRes(c, LAND_ERAS.noArtificerBp) : c;
};

/** What a branch costs a tower: a share of its level 4 price (docs/OBRAS.md §3). */
export const branchCost = (s: CampaignState, q: number, r: number, structure: StructureType): Resources => scaleRes(levelUpCost(s, q, r, structure, 4), BRANCH_COST_BP);

/** What mounting a tower on a wall run costs: the tower raised to its mounted level. */
export const mountCost = (s: CampaignState, q: number, r: number, tower: StructureType): Resources => {
  const out = res({ ...baseCost(s, tower) });
  for (let n = 2; n <= MOUNT_LEVEL; n++) addRes(out, levelUpCost(s, q, r, tower, n as StructLevel));
  return out;
};

/** What the next material costs: stone always, and iron on top for the iron cladding. */
export const materialUpCost = (s: CampaignState, q: number, r: number, structure: StructureType, level: StructLevel, to: 2 | 3): Resources =>
  costAt(s, q, r, withIron(materialCost(baseCost(s, structure), level), to === 3 ? MATERIAL3_IRON_PER_LEVEL * level : 0));

const remaining = (j: Job): Resources =>
  res({ wood: j.cost.wood - j.paid.wood, stone: j.cost.stone - j.paid.stone, crystal: j.cost.crystal - j.paid.crystal, dust: 0, iron: (j.cost.iron ?? 0) - (j.paid.iron ?? 0) });

/** Note text is free-form but bounded. */
const cleanText = (t: string, max: number): string => {
  let out = '';
  for (const ch of String(t)) out += ch.charCodeAt(0) < 32 ? ' ' : ch;
  return out.trim().slice(0, max);
};

/**
 * Applies one bastion command from `pid` at real time `now` (SPEC §10.2). Golem work is advanced
 * to `now` first. Invalid commands change nothing and return a reason.
 */
export const applyCampaignCommand = (s: CampaignState, pid: string, c: CampaignCommand, now: Ms, day = 0): CommandResult => {
  // The saga goes on after a won campaign (its last chapter is the true ending).
  const saga = c.t === 'sagaChoice' || c.t === 'sagaVote' || c.t === 'sagaRerun' || c.t === 'sagaRerunNext' || c.t === 'sideGo' || c.t === 'sideChoice';
  if (s.status !== 'active' && !(s.status === 'won' && saga)) return fail('campaignOver');
  const p = findPlayer(s, pid);
  if (!p || p.left) return fail('notAPlayer');
  if (now < s.clock) now = s.clock;
  advanceTime(s, now);
  if (settleSagaVote(s, now)) s.version++;
  if (settleSideVotes(s, now)) s.version++;
  const had = { ...s.storage };
  const r =
    c.t === 'sagaChoice'
      ? applySagaChoice(s, pid, c.act, c.choice, now, day, c.chapter)
      : c.t === 'sagaVote'
        ? applySagaVote(s, pid, c.chapter, c.act, c.choice, now)
        : c.t === 'sagaRerun'
          ? applySagaRerun(s, pid, c.chapter)
          : c.t === 'sagaRerunNext'
            ? applySagaRerunNext(s, pid, c.chapter, c.act, now)
            : c.t === 'sideGo'
              ? applySideGo(s, pid, c.saga, c.chapter, c.act, now)
              : c.t === 'sideChoice'
                ? applySideChoice(s, pid, c.saga, c.chapter, c.act, c.choice, now)
                : apply(s, pid, c, now, day);
  if (r.ok) {
    creditStorage(s, pid, c.t, had);
    p.lastActiveAt = now;
    s.version++;
  }
  return r;
};

/** A hero's name as kept: one line, no control characters, spaces folded, at most ROSTER.nameMax characters. */
const heroNameOf = (raw: unknown): string =>
  typeof raw === 'string'
    ? [...raw.replace(/\p{Cc}/gu, ' ').replace(/\s+/g, ' ').trim()].slice(0, ROSTER.nameMax).join('').trim()
    : '';

const apply = (s: CampaignState, pid: string, cmd: CampaignCommand, now: Ms, day: number): CommandResult => {
  const p = findPlayer(s, pid);
  if (!p) return fail('notAPlayer');
  // On terraced land, a command on any cell a three-cell building takes is about the building.
  let c = cmd;
  if (c.t !== 'blueprint' && 'q' in c && 'r' in c && typeof c.q === 'number' && typeof c.r === 'number' && isPart(s.cells[hexKey({ q: c.q, r: c.r })])) {
    c = { ...c, ...anchorOf(s, c.q, c.r) } as CampaignCommand;
  }
  const cls = p.hero.heroClass;
  switch (c.t) {
    case 'blueprint': {
      // A big building's turn, or a boulder's way down (docs/SABERES.md §4.1).
      const turn = s.map.land && (isBig(c.s) || c.s === 'boulder') ? (c.turn ?? 0) : 0;
      if (!Number.isInteger(turn) || turn < 0 || turn > 5) return fail('badStructure');
      if (c.s === 'gate') {
        if (!isGateCell(s, c.q, c.r)) return fail('notAGateway');
      } else {
        if (!BUILDABLE_TYPES.includes(c.s) && !(s.map.land && LAND_ONLY_TYPES.includes(c.s))) return fail('badStructure');
        if (!isBuildableCell(s, c.q, c.r)) return fail('notBuildable');
      }
      const key = hexKey(c);
      if (s.cells[key]) return fail('occupied');
      if (s.map.land) {
        const why = placeFail(s, c.s, c.q, c.r, turn);
        if (why) return fail(why);
        // A boulder sits at the top of a slope: the cell it rolls into is one level lower.
        if (c.s === 'boulder' && !boulderSlope(s, c.q, c.r, turn)) return fail('needsSlope');
      }
      // A gate tower stands beside a gate (built or planned).
      if (c.s === 'gatetower' && !DIRS.some((d) => s.cells[`${c.q + d.q},${c.r + d.r}`]?.built?.structure === 'gate' || s.cells[`${c.q + d.q},${c.r + d.r}`]?.job?.structure === 'gate')) return fail('needsGate');
      const def = STRUCTURES[c.s];
      // What a saber opens can't be laid before the bastion knows it (docs/SABERES.md §2, rule 4).
      if (saberLocked(s, SABER_STRUCTURES[c.s])) return fail('locked');
      // An ingenio is chosen as the tower is laid: its own tower, its saber known (docs/SABERES.md §4.2).
      const ing = c.branch ? BRANCHES[c.branch] : null;
      if (c.branch && (!ing || !ing.ingenio || ing.tower !== c.s || !s.map.land || saberLocked(s, ing.ingenio))) return fail('badBranch');
      if (ing && eraOf(s) < ing.era) return fail('eraLocked');
      if (def.unlockLevel > p.hero.level) return fail('locked');
      if (eraOf(s) < minEraOf(s, c.s)) return fail('eraLocked');
      if (def.unique && countOf(s, c.s) > 0) return fail('unique');
      if (def.needs && !standing(s, def.needs)) return fail('needsBuilding');
      const cap = towerCap(s, c.s);
      if (cap && cap.n >= cap.cap) return fail('eraCap');
      const job: Job = {
        id: s.nextId++,
        kind: 'new',
        structure: c.s,
        level: 1,
        material: 1,
        cost: costAt(s, c.q, c.r, { ...baseCost(s, c.s) }),
        paid: res({}),
        stage: 'blueprint',
        progressMs: 0,
        totalMs: BUILD_TIME_MS[0],
        placedBy: pid,
        placedAt: now,
        fundedAt: 0,
        ...(turn ? { turn } : {}),
        ...(c.branch ? { branch: c.branch } : {}),
      };
      s.cells[key] = { built: null, job };
      placeParts(s, key);
      event(s, now, 'blueprint', { pid, cellKey: key, data: { s: c.s, ...(c.branch ? { branch: c.branch } : {}) } });
      return OK;
    }
    case 'elixir': {
      // The laboratory brews one elixir for the next wave, paid in dust.
      if (!standing(s, 'lab')) return fail('needsLab');
      if (!ELIXIR_KINDS.includes(c.kind)) return fail('badElixir');
      const ready = !!s.elixir && s.elixir.marea === s.mareaIndex && s.elixir.wave === s.waveIndex;
      // On terraced land a laboratory raised higher brews a second, different elixir for the same wave.
      const two = s.map.land && (LAND_LEVELS.lab.elixirs[bestLevel(s, 'lab') - 1] ?? 1) >= 2;
      if (ready && (!two || s.elixir?.also || s.elixir?.kind === c.kind)) return fail('elixirReady');
      const dust = ELIXIRS[c.kind].dustPerPlayer * s.players.filter((x) => !x.left).length;
      if (s.storage.dust < dust) return fail('notEnough');
      s.storage.dust -= dust;
      if (ready && s.elixir) s.elixir.also = c.kind;
      else s.elixir = { kind: c.kind, marea: s.mareaIndex, wave: s.waveIndex, by: pid };
      event(s, now, 'elixir', { pid, data: { kind: c.kind, dust } });
      return OK;
    }
    case 'greatWork': {
      if (s.cells[GREAT_WORK_KEY]) return fail('greatWorkUnderway');
      const next = nextDistrict(s);
      if (!next) return fail('noMoreDistricts');
      if (groupBelow(s, now)) return fail('heroesTooLow');
      // Eras II and III raise a district on a side; the later ones crown the keep (no side, no kind).
      if (!next.crown) {
        if (c.kind === undefined || c.side === undefined || !DISTRICT_KINDS.includes(c.kind)) return fail('badDistrict');
        if (!withDistrict(s.map, { side: c.side, kind: c.kind })) return fail('sideBlocked');
      }
      const job: Job = {
        id: s.nextId++,
        kind: 'district',
        structure: 'rampart',
        level: 1,
        material: 1,
        cost: greatWorkCost(s),
        paid: res({}),
        stage: 'blueprint',
        progressMs: 0,
        totalMs: next.buildMs,
        placedBy: pid,
        placedAt: now,
        fundedAt: 0,
        ...(next.crown || c.side === undefined || c.kind === undefined ? {} : { district: { side: c.side, kind: c.kind } }),
      };
      s.cells[GREAT_WORK_KEY] = { built: null, job };
      event(s, now, 'blueprint', { pid, cellKey: GREAT_WORK_KEY, data: { s: 'greatWork', ...(job.district ? { side: job.district.side, kind: job.district.kind } : { crown: true }) } });
      return OK;
    }
    case 'contribute': {
      const key = hexKey(c);
      const cell = s.cells[key];
      const j = cell?.job;
      if (!j || j.stage !== 'blueprint') return fail('noBlueprint');
      if (!isNonNegRes(c.res)) return fail('badAmount');
      const left = remaining(j);
      const give = res({
        wood: (c.res.wood ?? 0) > left.wood ? left.wood : c.res.wood ?? 0,
        stone: (c.res.stone ?? 0) > left.stone ? left.stone : c.res.stone ?? 0,
        crystal: (c.res.crystal ?? 0) > left.crystal ? left.crystal : c.res.crystal ?? 0,
        iron: (c.res.iron ?? 0) > left.iron ? left.iron : c.res.iron ?? 0,
      });
      if (sumRes(give) === 0) return fail('nothingToGive');
      if (!hasRes(s.storage, give)) return fail('notEnough');
      addRes(s.storage, give, -1);
      addRes(j.paid, give);
      creditGiven(s, pid, give);
      event(s, now, 'contribute', { pid, cellKey: key, data: { wood: give.wood, stone: give.stone, crystal: give.crystal, ...(give.iron ? { iron: give.iron } : {}) } });
      if (sumRes(remaining(j)) === 0) {
        j.stage = 'building';
        j.fundedAt = now;
      }
      return OK;
    }
    case 'work': {
      const key = hexKey(c);
      const j = s.cells[key]?.job;
      if (!j || j.stage !== 'building') return fail('notBuilding');
      if (!Number.isInteger(c.minutes) || c.minutes < 1 || c.minutes > 60) return fail('badAmount');
      j.progressMs += workProgressMs(p.hero, c.minutes);
      creditWork(s, pid, c.minutes);
      event(s, now, 'work', { pid, cellKey: key, data: { minutes: c.minutes } });
      if (j.progressMs >= j.totalMs) completeJob(s, key, now);
      return OK;
    }
    case 'repair': {
      const key = hexKey(c);
      const b = s.cells[key]?.built;
      if (!b) return fail('nothingThere');
      const cell = s.cells[key] as CellState;
      if (cell.job) return fail('busy');
      // Under the props rite the gates aren't repaired between the Marea's waves (docs/SABERES.md §4.3).
      if (b.structure === 'gate' && propsHold(s)) return fail('propsHold');
      const quote = repairQuote(s, key, p, now);
      if (!quote) return fail('intact');
      const { cost, ms: total } = quote;
      if (!hasRes(s.storage, cost)) return fail('notEnough');
      addRes(s.storage, cost, -1);
      cell.job = {
        id: s.nextId++,
        kind: 'repair',
        structure: b.structure,
        level: b.level,
        material: b.material,
        cost,
        paid: { ...cost },
        stage: 'building',
        progressMs: 0,
        totalMs: total,
        placedBy: pid,
        placedAt: now,
        fundedAt: now,
      };
      event(s, now, 'repairOrdered', { pid, cellKey: key });
      return OK;
    }
    case 'upgrade': {
      const key = hexKey(c);
      const cell = s.cells[key];
      const b = cell?.built;
      if (!cell || !b) return fail('nothingThere');
      if (cell.job) return fail('busy');
      if (b.structure === 'gate' || b.structure === 'wall' || b.structure === 'rampart' || ((b.structure === 'workshop' || b.structure === 'lab') && !s.map.land)) return fail('noLevels');
      // Towers reach level 4 from era II (with iron), the forge from era III; everything else stops at 3.
      const top = levelTop(s, b.structure);
      if (b.level >= top) return fail(levelEraLocked(s, b.structure, b.level) ? 'eraLocked' : 'maxLevel');
      const next = (b.level + 1) as StructLevel;
      // On the ring an artificer has to order it; on terraced land it costs more without one in the group (levelUpCost).
      if (next >= 3 && cls !== 'artificer' && !s.map.land) return fail('needsArtificer');
      if (b.hp <= 0 && STRUCTURES[b.structure].hp > 0) return fail('ruined');
      cell.job = {
        id: s.nextId++,
        kind: 'level',
        structure: b.structure,
        level: next,
        material: b.material,
        cost: levelUpCost(s, c.q, c.r, b.structure, next),
        paid: res({}),
        stage: 'blueprint',
        progressMs: 0,
        totalMs: BUILD_TIME_MS[next - 1] as number,
        placedBy: pid,
        placedAt: now,
        fundedAt: 0,
      };
      event(s, now, 'blueprint', { pid, cellKey: key, data: { s: b.structure, level: next } });
      return OK;
    }
    case 'material': {
      const key = hexKey(c);
      const cell = s.cells[key];
      const b = cell?.built;
      if (!cell || !b) return fail('nothingThere');
      if (cell.job) return fail('busy');
      if (b.material >= 3) return fail('maxMaterial');
      if (STRUCTURES[b.structure].category === 'trap' || b.structure === 'rampart') return fail('noMaterial');
      // Stone, then iron (era II, and only the iron mines give it).
      const to = (b.material + 1) as 2 | 3;
      if (to === 3 && eraOf(s) < 2) return fail('eraLocked');
      if (p.hero.level < MATERIAL_UNLOCK_LEVEL[to]) return fail(to === 2 ? 'stoneLocked' : 'ironLocked');
      cell.job = {
        id: s.nextId++,
        kind: 'material',
        structure: b.structure,
        level: b.level,
        material: to,
        cost: materialUpCost(s, c.q, c.r, b.structure, b.level, to),
        paid: res({}),
        stage: 'blueprint',
        progressMs: 0,
        totalMs: mulBp(BUILD_TIME_MS[b.level - 1] as number, MATERIAL_UPGRADE_TIME_BP),
        placedBy: pid,
        placedAt: now,
        fundedAt: 0,
      };
      event(s, now, 'blueprint', { pid, cellKey: key, data: { s: b.structure, material: to } });
      return OK;
    }
    case 'branch': {
      // A level 4 tower on terraced land takes one of its two branches; changing it later pays the other in full.
      if (!s.map.land) return fail('badStructure');
      const key = hexKey(c);
      const cell = s.cells[key];
      const b = cell?.built;
      if (!cell || !b) return fail('nothingThere');
      if (cell.job) return fail('busy');
      const def = BRANCHES[c.b];
      if (!def || def.tower !== b.structure) return fail('badBranch');
      // An ingenio is chosen only as the tower is laid, and keeps its way: no branch to it or from it at level 4.
      if (def.ingenio || (b.branch && BRANCHES[b.branch].ingenio)) return fail('badBranch');
      if (b.branch === c.b) return fail('sameBranch');
      if (eraOf(s) < def.era) return fail('eraLocked');
      if (b.level < 4) return fail('branchLevel');
      if (b.hp <= 0) return fail('ruined');
      cell.job = {
        id: s.nextId++,
        kind: 'branch',
        structure: b.structure,
        level: b.level,
        material: b.material,
        branch: c.b,
        cost: branchCost(s, c.q, c.r, b.structure),
        paid: res({}),
        stage: 'blueprint',
        progressMs: 0,
        totalMs: hoursMs(BRANCH_TIME_HOURS),
        placedBy: pid,
        placedAt: now,
        fundedAt: 0,
      };
      event(s, now, 'blueprint', { pid, cellKey: key, data: { s: b.structure, branch: c.b } });
      return OK;
    }
    case 'earth': {
      // An earthwork: the land itself changes when the golems are done (docs/OBRAS.md §5).
      if (!EARTH_IDS.includes(c.e)) return fail('badEarth');
      const why = earthFail(s, c.q, c.r, c.e);
      if (why) return fail(why);
      const key = hexKey(c);
      s.cells[key] = { built: null, job: earthJob(s, c.e, pid, now) };
      event(s, now, 'blueprint', { pid, cellKey: key, data: { earth: c.e } });
      return OK;
    }
    case 'mount': {
      // The adarve (era III, terraced land): a tower on top of a stone or iron wall run; it falls with the run.
      if (!s.map.land) return fail('badStructure');
      const key = hexKey(c);
      const cell = s.cells[key];
      const b = cell?.built;
      if (!cell || !b || b.structure !== 'wall') return fail('needsWall');
      if (cell.job) return fail('busy');
      if (b.mount) return fail('occupied');
      if (!MOUNTS.includes(c.s)) return fail('badStructure');
      if (eraOf(s) < MOUNT_ERA) return fail('eraLocked');
      if (b.material < 2) return fail('needsStoneWall');
      if (b.hp <= 0) return fail('ruined');
      const cap = towerCap(s, c.s);
      if (cap && cap.n >= cap.cap) return fail('eraCap');
      cell.job = {
        id: s.nextId++,
        kind: 'mount',
        structure: 'wall',
        level: MOUNT_LEVEL,
        material: b.material,
        mount: c.s,
        cost: mountCost(s, c.q, c.r, c.s),
        paid: res({}),
        stage: 'blueprint',
        progressMs: 0,
        totalMs: BUILD_TIME_MS[MOUNT_LEVEL - 1] as number,
        placedBy: pid,
        placedAt: now,
        fundedAt: 0,
      };
      event(s, now, 'blueprint', { pid, cellKey: key, data: { s: c.s, mount: 1 } });
      return OK;
    }
    case 'cancel': {
      const key = hexKey(c);
      const cell = s.cells[key];
      const j = cell?.job;
      if (!cell || !j) return fail('noJob');
      // A blueprint, or a job the golems only just took up, gives back everything paid; past that, half (like demolishing).
      addRes(s.storage, j.stage === 'blueprint' || j.progressMs < CANCEL_GRACE_MS ? j.paid : scaleRes(j.paid, DEMOLISH_REFUND_BP));
      // An upgrade or repair called off leaves the structure as it was.
      if (!cell.built) {
        clearParts(s, key);
        delete s.cells[key];
      }
      cell.job = null;
      return OK;
    }
    case 'demolish': {
      const key = hexKey(c);
      const cell = s.cells[key];
      if (!cell) return fail('nothingThere');
      // The ring's sectors need their gates; on terraced land a gate is a piece the group set down like any other.
      if (!s.map.land && (cell.built?.structure === 'gate' || cell.built?.structure === 'rampart')) return fail('cannotDemolishGate');
      const text = cleanText(c.note, 140);
      if (text.length === 0) return fail('noteRequired');
      const refund = res({});
      if (cell.job) addRes(refund, scaleRes(cell.job.paid, DEMOLISH_REFUND_BP));
      if (cell.built) addRes(refund, scaleRes(baseCost(s, cell.built.structure), DEMOLISH_REFUND_BP));
      addRes(s.storage, refund);
      const prev = JSON.parse(JSON.stringify(cell)) as typeof cell;
      s.notes.push({ id: s.nextId++, cellKey: key, by: pid, at: now, text, prev, refund, undone: false });
      if (s.notes.length > 100) s.notes.splice(0, s.notes.length - 100);
      clearParts(s, key);
      delete s.cells[key];
      if (p.fortified === key) p.fortified = null;
      for (const pl of s.players) pl.enchanted = pl.enchanted.filter((k) => k !== key);
      event(s, now, 'demolished', { pid, cellKey: key, data: { note: text } });
      return OK;
    }
    case 'undoDemolish': {
      const n = s.notes.find((x) => x.id === c.note);
      if (!n || n.undone) return fail('noNote');
      if (now - n.at > DEMOLISH_UNDO_MS) return fail('tooLate');
      if (s.cells[n.cellKey]) return fail('occupied');
      const was = n.prev.built ?? n.prev.job;
      if (s.map.land && was) {
        const at = parseHexKey(n.cellKey);
        if (placeFail(s, was.structure, at.q, at.r, was.turn ?? 0) === 'occupied') return fail('occupied');
      }
      if (!hasRes(s.storage, n.refund)) return fail('notEnough');
      addRes(s.storage, n.refund, -1);
      s.cells[n.cellKey] = JSON.parse(JSON.stringify(n.prev)) as typeof n.prev;
      placeParts(s, n.cellKey);
      n.undone = true;
      event(s, now, 'undoDemolish', { pid, cellKey: n.cellKey });
      return OK;
    }
    case 'enchant': {
      if (cls !== 'mage') return fail('needsMage');
      if (!ENCHANTS.includes(c.element)) return fail('badElement');
      const lab = standing(s, 'lab');
      if (c.element === 'acid' && !lab) return fail('needsLab');
      const key = hexKey(c);
      const b = s.cells[key]?.built;
      if (!b || STRUCTURES[b.structure].category !== 'tower' || b.structure === 'beacon') return fail('notATower');
      b.enchant = { element: c.element, until: now + (lab ? mulBp(hoursMs(24), LAB.enchantBp) : hoursMs(24)), by: pid };
      p.enchanted = p.enchanted.filter((k) => k !== key && (s.cells[k]?.built?.enchant?.by === pid));
      p.enchanted.push(key);
      while (p.enchanted.length > MAX_ENCHANTS) {
        const old = p.enchanted.shift() as string;
        const ob = s.cells[old]?.built;
        if (ob && ob.enchant?.by === pid) ob.enchant = null;
      }
      event(s, now, 'enchant', { pid, cellKey: key, data: { element: c.element } });
      return OK;
    }
    case 'golems': {
      // Terraced land: the group's table of gatherers; whatever it leaves builds and repairs.
      if (!s.map.land) return fail('noGolemTable');
      const iron = c.iron ?? 0;
      const n = [c.wood, c.stone, c.crystal, iron];
      if (!n.every((x) => Number.isInteger(x) && x >= 0)) return fail('badAmount');
      // The ones a saber keeps (the crane, the sieve's sift) can't be sent gathering.
      if (c.wood + c.stone + c.crystal + iron > golemsFor(s) - keptGolems(s, now)) return fail('tooManyGolems');
      s.golems = { wood: c.wood, stone: c.stone, crystal: c.crystal, ...(iron > 0 ? { iron } : {}) };
      event(s, now, 'golems', { pid, data: { wood: c.wood, stone: c.stone, crystal: c.crystal, iron } });
      return OK;
    }
    case 'fortify': {
      if (cls !== 'warrior') return fail('needsWarrior');
      const key = hexKey(c);
      const b = s.cells[key]?.built;
      if (!b || (b.structure !== 'wall' && b.structure !== 'gate')) return fail('notAWall');
      if (p.fortified && p.fortified !== key) {
        const ob = s.cells[p.fortified]?.built;
        if (ob) {
          ob.fortifiedUntil = 0;
          const m = builtMaxHp(ob, now);
          if (ob.hp > m) ob.hp = m;
        }
      }
      const before = builtMaxHp(b, now);
      b.fortifiedUntil = now + hoursMs(24);
      b.hp += builtMaxHp(b, now) - before;
      p.fortified = key;
      event(s, now, 'fortify', { pid, cellKey: key });
      return OK;
    }
    case 'ghostPost': {
      const post = c.post;
      if (post && 'sector' in post && (post.sector < 0 || post.sector >= s.map.sectors.length)) return fail('badSector');
      p.ghostPost = post;
      event(s, now, 'ghostPost', { pid, data: { post: post ? ('sector' in post ? post.sector : -1) : -2 } });
      return OK;
    }
    case 'adopt': {
      if (c.sector < 0 || c.sector >= s.map.sectors.length) return fail('badSector');
      if (!p.adoptedSectors.includes(c.sector)) p.adoptedSectors.push(c.sector);
      if (p.adoptedSectors.length > 2) p.adoptedSectors.shift();
      return OK;
    }
    case 'hireMercenary': {
      if (!HERO_CLASSES.includes(c.cls)) return fail('badClass');
      if (barracksLevel(s) === 0) return fail('needsBarracks');
      const mercs = servingMercenaries(s);
      if (mercs.length >= mercenarySlots(s)) return fail('noMercSlot');
      const cost = mercenaryCost(s);
      if (!hasRes(s.storage, cost)) return fail('notEnough');
      addRes(s.storage, cost, -1);
      mercs.push({ cls: c.cls, marea: s.mareaIndex, by: pid });
      s.mercs = mercs;
      event(s, now, 'mercenary', { pid, data: { cls: c.cls } });
      return OK;
    }
    case 'skill': {
      const node = SKILL_NODES[c.node];
      if (!node || node.cls !== cls) return fail('badNode');
      const h = p.hero;
      if (pointsSpent(h) >= h.skillPoints) return fail('noPoints');
      const cur = h.skills[c.node] ?? 0;
      if (cur >= node.maxRank) return fail('maxRank');
      const e = node.effect;
      // A tome's skill comes with its pages, never with points.
      if (e.kind === 'learn' && e.tome) return fail('needsTome');
      if (e.kind === 'learn' && h.level < e.level) return fail('needsLevel');
      // A variant needs its skill known (a class's own skills are known from the start).
      if (e.kind === 'variant' && node.branch < 3 && !knownSkills(h, node.branch as 0 | 1 | 2).includes(e.skill)) return fail('needsSkill');
      if (e.kind === 'bastion' && node.tier > 1) {
        const prev = SKILL_TREES[cls].find((n) => n.branch === node.branch && n.tier === node.tier - 1);
        if (prev && (h.skills[prev.id] ?? 0) === 0) return fail('needsPrevious');
      }
      h.skills[c.node] = (cur + 1) as 1 | 2 | 3;
      // A first variant goes where its skill was carried alone: learning it is meant to be used.
      if (e.kind === 'variant' && cur === 0) {
        for (const l of h.loadouts ?? []) for (const pk of l.picks) if (pk.skill === e.skill && pk.variant === null) pk.variant = e.fork;
      }
      return OK;
    }
    case 'newHero': {
      if (!HERO_CLASSES.includes(c.cls)) return fail('badClass');
      const bench = p.bench ?? [];
      if (1 + bench.length >= ROSTER.max) return fail('rosterFull');
      // The one being played waits on the bench as it is; the new one starts at level 1 and its spectre learns anew.
      bench.push({ hero: p.hero, ghostProfile: p.ghostProfile });
      p.bench = bench;
      const name = heroNameOf(c.name);
      p.hero = { ...(name ? { name } : {}), heroClass: c.cls, level: 1, xp: 0, skillPoints: 0, skills: {}, gear: {}, bag: [], relics: [] };
      p.ghostProfile = DEFAULT_PROFILE(c.cls);
      event(s, now, 'heroNew', { pid, data: { cls: c.cls } });
      return OK;
    }
    case 'nameHero': {
      if (typeof c.name !== 'string') return fail('badName');
      const name = heroNameOf(c.name);
      if (name) p.hero.name = name;
      else delete p.hero.name;
      return OK;
    }
    case 'switchHero': {
      const bench = p.bench ?? [];
      const b = Number.isInteger(c.index) ? bench[c.index] : undefined;
      if (!b) return fail('noHero');
      bench[c.index] = { hero: p.hero, ghostProfile: p.ghostProfile };
      p.hero = b.hero;
      p.ghostProfile = b.ghostProfile;
      event(s, now, 'heroSwitched', { pid, data: { cls: b.hero.heroClass, level: b.hero.level } });
      return OK;
    }
    case 'retireHero': {
      const bench = p.bench ?? [];
      const b = Number.isInteger(c.index) ? bench[c.index] : undefined;
      if (!b) return fail('noHero');
      bench.splice(c.index, 1);
      if (bench.length === 0) delete p.bench;
      event(s, now, 'heroRetired', { pid, data: { cls: b.hero.heroClass, level: b.hero.level } });
      return OK;
    }
    case 'aura': {
      if (p.hero.heroClass !== 'paladin' || !PALADIN_AURAS.includes(c.aura)) return fail('badClass');
      p.hero.aura = c.aura;
      return OK;
    }
    case 'respec': {
      const h = p.hero;
      const spent = pointsSpent(h);
      if (spent === 0) return fail('nothingSpent');
      if (!h.freeRespec) {
        const cost = RESPEC.dustPerPoint * spent;
        if (s.storage.dust < cost) return fail('notEnough');
        s.storage.dust -= cost;
      }
      h.freeRespec = false;
      h.skills = {};
      return OK;
    }
    case 'loadout': {
      const h = p.hero;
      const have = h.loadouts ?? [];
      if (!Number.isInteger(c.index) || c.index < 0 || c.index >= LOADOUTS.count || c.index > have.length) return fail('badLoadout');
      const l = c.loadout;
      if (!l || !Array.isArray(l.picks) || l.picks.length !== 3) return fail('badLoadout');
      for (const slot of [0, 1, 2] as const) {
        const pk = l.picks[slot];
        if (!pk || !knownSkills(h, slot).includes(pk.skill)) return fail('badLoadout');
        if (pk.variant !== null && !knownVariants(h, pk.skill).includes(pk.variant)) return fail('badLoadout');
      }
      if (l.aura !== undefined && (h.heroClass !== 'paladin' || !PALADIN_AURAS.includes(l.aura))) return fail('badLoadout');
      const name = typeof l.name === 'string' ? l.name.trim().slice(0, LOADOUTS.nameMax) : '';
      const picks = l.picks.map((pk) => ({ skill: pk.skill, variant: pk.variant })) as Loadout['picks'];
      // Glyphs (docs/GLIFOS.md): each one complete, on an open slot, fitting its skill, and once.
      let glyphs: Loadout['glyphs'] | null = null;
      if (l.glyphs !== undefined) {
        if (!Array.isArray(l.glyphs) || l.glyphs.length !== 3) return fail('badLoadout');
        glyphs = carvedGlyphs(h, picks, l.glyphs);
        const asked = l.glyphs.filter((g) => g !== null).length;
        if (asked !== (glyphs?.filter((g) => g !== null).length ?? 0)) return fail('badGlyph');
      }
      // The pieces it goes in with (docs/DECISIONS.md 2026-11-04): the hero's own, each once, each wearable in its slot.
      let gear: Loadout['gear'] | null = null;
      if (l.gear !== undefined) {
        if (!l.gear || typeof l.gear !== 'object') return fail('badLoadout');
        const ids = new Set<number>();
        for (const [k, id] of Object.entries(l.gear)) {
          if (!WORN_SLOTS.includes(k as WornSlot) || !Number.isInteger(id) || ids.has(id as number)) return fail('badGear');
          ids.add(id as number);
        }
        gear = {};
        for (const slot of WORN_SLOTS) if (l.gear[slot] !== undefined) gear[slot] = l.gear[slot];
        const worn = preparedGear(h, gear);
        for (const slot of WORN_SLOTS) if (gear[slot] !== undefined && worn[slot]?.id !== gear[slot]) return fail('badGear');
      }
      // Unique forms (docs/UNICAS.md §3): a or b, for a piece of its class worn in that slot (in this preparation) and awakened to the II.
      let forms: Loadout['forms'] | null = null;
      if (l.forms !== undefined) {
        if (!l.forms || typeof l.forms !== 'object') return fail('badLoadout');
        for (const k of Object.keys(l.forms)) if (k !== 'weapon' && k !== 'armor' && k !== 'amulet') return fail('badForm');
        forms = chosenForms(gear ? { ...h, gear: preparedGear(h, gear) } : h, l.forms);
        const asked = Object.values(l.forms).filter((x) => x !== undefined).length;
        if (asked !== Object.keys(forms ?? {}).length) return fail('badForm');
      }
      have[c.index] = { ...(name ? { name } : {}), picks, ...(l.aura ? { aura: l.aura } : {}), ...(glyphs ? { glyphs } : {}), ...(forms ? { forms } : {}), ...(gear ? { gear } : {}) };
      h.loadouts = have;
      return OK;
    }
    case 'carry': {
      const h = p.hero;
      if (!CARRY_MODES.includes(c.mode)) return fail('badMode');
      const n = h.loadouts?.length ?? 0;
      if (!Number.isInteger(c.index) || c.index < 0 || c.index >= imax(1, n)) return fail('badLoadout');
      h.carry = { ...h.carry, [c.mode]: c.index };
      return OK;
    }
    case 'equip': {
      const block = equipItem(p, c.item, c.ring);
      return block ? fail(block) : OK;
    }
    case 'forge': {
      const cap = forgeMaxPlus(s);
      if (!cap) return fail('needsForge');
      const item = findItem(p, c.item);
      if (!item) return fail('noItem');
      if (item.plus >= FORGE.maxPlus) return fail('maxPlus');
      if (item.plus >= cap) return fail('forgeLevel');
      // The player picks which attribute the plus goes to (any the item has); the main one by default.
      const attr = c.attr ?? mainAttr(item);
      if (!attr || item.attrs[attr] === undefined) return fail('noAttr');
      const cost = forgeCost(item);
      if (s.storage.dust < cost) return fail('notEnough');
      s.storage.dust -= cost;
      const forged = { ...forgedOf(item) };
      forged[attr] = (forged[attr] ?? 0) + 1;
      item.forged = forged;
      item.plus++;
      event(s, now, 'forged', { pid, data: { item: item.id, slot: item.slot, rarity: item.rarity, attr, plus: item.plus, dust: cost } });
      // A unique piece's work is its slot's (docs/PANOPLIA.md §3): the other pieces of the slot get it too.
      syncSlot(p.hero, item);
      return OK;
    }
    case 'temper': {
      // Embers from Ascuas raise a piece one rarity step at the forge, keeping what it has.
      if (!forgeMaxPlus(s)) return fail('needsForge');
      const item = findItem(p, c.item);
      if (!item) return fail('noItem');
      if (item.unique) return fail('bound');
      // Tempering goes up to rare: a very rare piece is already past it.
      if (item.rarity === 'rare' || item.rarity === 'veryRare') return fail('maxRarity');
      const to = item.rarity === 'common' ? 'uncommon' : 'rare';
      const cost = TEMPER[to];
      if ((p.hero.embers ?? 0) < cost.embers) return fail('notEnoughEmbers');
      if (s.storage.dust < cost.dust) return fail('notEnough');
      p.hero.embers = (p.hero.embers ?? 0) - cost.embers;
      s.storage.dust -= cost.dust;
      temperItem(s, item, to);
      event(s, now, 'temper', { pid, data: { item: item.id, slot: item.slot, rarity: to, dust: cost.dust } });
      return OK;
    }
    case 'carve': {
      // Obsidian shards (level III) carve a rare piece's effect into another one, drawn at random.
      if (!forgeMaxPlus(s)) return fail('needsForge');
      const item = findItem(p, c.item);
      if (!item) return fail('noItem');
      if (item.unique) return fail('bound');
      if ((item.rarity !== 'rare' && item.rarity !== 'veryRare') || !item.effect) return fail('notRare');
      if ((p.hero.shards ?? 0) < CARVE.shards) return fail('notEnoughShards');
      if (s.storage.dust < CARVE.dust) return fail('notEnough');
      p.hero.shards = (p.hero.shards ?? 0) - CARVE.shards;
      s.storage.dust -= CARVE.dust;
      const was = item.effect;
      const others = effectPool(item).filter((e) => e !== was);
      item.effect = others[rngInt(s.rng, others.length)] as typeof item.effect;
      event(s, now, 'carve', { pid, data: { item: item.id, slot: item.slot, from: was, to: item.effect as string, dust: CARVE.dust } });
      return OK;
    }
    case 'cure': {
      // A condition that lasts days: a cleric cures anyone for free once a day (the friend needn't be there);
      // otherwise the temple does it for dust from the storage.
      const target = s.players.find((x) => x.id === c.target);
      if (!target?.hero.ailment || target.hero.ailment.until <= now) return fail('noAilment');
      const cleric = p.hero.heroClass === 'cleric' && (p.hero.curedAt === undefined || now - p.hero.curedAt >= AILMENT_CURE.clericHours * 3_600_000);
      let dust = 0;
      if (cleric) p.hero.curedAt = now;
      else {
        if (!standing(s, 'temple')) return fail('needsTemple');
        if (s.storage.dust < AILMENT_CURE.templeDust) return fail('notEnough');
        dust = AILMENT_CURE.templeDust;
        s.storage.dust -= dust;
      }
      const kind = target.hero.ailment.kind;
      delete target.hero.ailment;
      // Tending a friend is the cleric's part between fights: it pays in XP.
      const xp = cleric && target.id !== pid ? AILMENT_CURE.clericXp : 0;
      if (xp) grantXp(s, pid, xp, now);
      event(s, now, 'cure', { pid, data: { target: target.id, kind, dust, cleric, ...(xp ? { xp } : {}) } });
      return OK;
    }
    case 'attune': {
      // Dust carves a piece's affinity into another damage type: the same value, its pluses kept.
      if (!forgeMaxPlus(s)) return fail('needsForge');
      const item = findItem(p, c.item);
      if (!item) return fail('noItem');
      if (item.unique) return fail('bound');
      const from = (Object.keys(item.attrs) as GearAttr[]).find(isResAttr);
      if (!from) return fail('noAttr');
      const to = RES_ATTRS.find((k) => RES_TYPE[k] === c.to);
      if (!to || to === from) return fail('noAttr');
      const cost = ATTUNE.dust[item.rarity];
      if (s.storage.dust < cost) return fail('notEnough');
      s.storage.dust -= cost;
      const swap = (r: Partial<Record<GearAttr, number>>): Partial<Record<GearAttr, number>> => {
        const out: Partial<Record<GearAttr, number>> = {};
        for (const k of Object.keys(r).sort() as GearAttr[]) out[k === from ? to : k] = r[k] as number;
        return out;
      };
      item.attrs = swap(item.attrs);
      if (item.forged) item.forged = swap(item.forged);
      event(s, now, 'attune', { pid, data: { item: item.id, slot: item.slot, from: RES_TYPE[from], to: c.to, dust: cost } });
      return OK;
    }
    case 'reforge': {
      // Dream dust turns one attribute of a piece into another it lacks, rolled anew.
      if (!forgeMaxPlus(s)) return fail('needsForge');
      const item = findItem(p, c.item);
      if (!item) return fail('noItem');
      if (item.attrs[c.attr] === undefined) return fail('noAttr');
      // A unique piece says what it becomes (and must); any other draws it, unless the hero directs it (level 38, triple dust).
      if (item.unique && c.to === undefined) return fail('noAttr');
      const directed = !item.unique && c.to !== undefined;
      if (directed && p.hero.level < DIRECTED_REFORGE.level) return fail('levelTooLow');
      if (c.to !== undefined && !reforgeChoices(item, c.attr).includes(c.to)) return fail('noAttr');
      const cost = directed ? mulBp(reforgeCost(item), DIRECTED_REFORGE.costBp) : reforgeCost(item);
      if (s.storage.dust < cost) return fail('notEnough');
      s.storage.dust -= cost;
      const to = reforgeItem(s, item, c.attr, c.to, !!item.unique, p.hero.level);
      if (!to) return fail('noAttr');
      event(s, now, 'reforge', { pid, data: { item: item.id, slot: item.slot, rarity: item.rarity, from: c.attr, to, dust: cost, next: reforgeCost(item) } });
      syncSlot(p.hero, item);
      return OK;
    }
    case 'awakenUnique': {
      // A unique piece wakes one stage at the forge: the hero's level, the forge's, a seal, dust and rares melted in.
      const item = findItem(p, c.item);
      if (!item) return fail('noItem');
      // Six scraps pay the dust and the rares, once the slot has every piece (docs/PANOPLIA.md §4).
      const byScraps = !!c.scraps;
      if (byScraps && !scrapsPay(p.hero, item)) return fail('notEnoughScraps');
      const block = awakenBlock(s, p, item, byScraps);
      if (block) return fail(block);
      const st = nextAwaken(item)!;
      const fuel = [...new Set(c.fuel)];
      // An ember of the dragon's heart (La Orilla 4) pays the dust and the rares: nothing melted in.
      const ember = !byScraps && fuel.length === 0 && (p.hero.dragonEmbers ?? 0) > 0;
      if (byScraps) p.hero.scraps = { ...p.hero.scraps, [item.slot as UniqueSlot]: (p.hero.scraps?.[item.slot as UniqueSlot] ?? 0) - PANOPLY.cost };
      else if (ember) p.hero.dragonEmbers = (p.hero.dragonEmbers ?? 0) - 1;
      else {
        if (fuel.length !== st.rares || s.storage.dust < st.dust) return fail('fuel');
        const pieces = fuel.map((id) => p.hero.bag.find((x) => x.id === id));
        if (pieces.some((x) => !x || x.rarity !== 'rare' || x.unique)) return fail('fuel');
        p.hero.bag = p.hero.bag.filter((x) => !fuel.includes(x.id));
        s.storage.dust -= st.dust;
      }
      if (sealSpent(item)) p.hero.seals = { ...p.hero.seals, [st.seal]: (p.hero.seals?.[st.seal] ?? 0) - 1 };
      awakenItem(item, p.hero.level);
      // The bastion's panoply shows the highest stage a rearmed piece reached (docs/PANOPLIA.md §9.6).
      for (const x of s.hung ?? []) if (UNIQUES[x.id].slot === item.slot && UNIQUES[x.id].cls === p.hero.heroClass && (item.awaken ?? 0) > x.stage) x.stage = item.awaken ?? 0;
      event(s, now, 'uniqueAwakened', { pid, data: { item: item.id, unique: item.unique ?? '', stage: item.awaken ?? 0, dust: ember ? 0 : st.dust, ...(ember ? { ember: true } : {}) } });
      syncSlot(p.hero, item);
      return OK;
    }
    case 'rearm': {
      // Hilde rearms a piece of the old garrison from six scraps (docs/PANOPLIA.md §4).
      const r = rearm(s, p, c.unique, now);
      return typeof r === 'string' ? fail(r) : OK;
    }
    case 'infuse': {
      // A rare burnt into a unique slot's effect (docs/PANOPLIA.md §6).
      const uq = findItem(p, c.item);
      const rare = [...p.hero.bag, ...wornItems(p.hero)].find((x) => x.id === c.rare);
      const block = infuseBlock(s, p, uq, rare);
      if (block) return fail(block);
      infuse(s, p, uq as ItemInstance, rare as ItemInstance, now);
      return OK;
    }
    case 'uninfuse': {
      const uq = findItem(p, c.item);
      if (!uq) return fail('noItem');
      const block = uninfuse(s, p, uq, now);
      return block ? fail(block) : OK;
    }
    case 'scrapSlot': {
      // The cellars' last scrap moved to another slot, once (docs/PANOPLIA.md §4).
      const h = p.hero;
      const from = h.cellarsScrap;
      if (!from || from === c.slot || !(h.scraps?.[from] ?? 0)) return fail('noScrap');
      if (!scrapsFall(h, c.slot) || (h.scraps?.[c.slot] ?? 0) >= scrapCap(h, c.slot)) return fail('scrapsFull');
      h.scraps = { ...h.scraps, [from]: (h.scraps?.[from] ?? 0) - 1, [c.slot]: (h.scraps?.[c.slot] ?? 0) + 1 };
      delete h.cellarsScrap;
      return OK;
    }
    case 'masterwork': {
      // A masterwork (level 48, once per hero): a very rare piece's attributes rise to the top of their range.
      if (!forgeMaxPlus(s)) return fail('needsForge');
      const item = findItem(p, c.item);
      if (!item) return fail('noItem');
      if (item.rarity !== 'veryRare' || item.masterwork) return fail('notVeryRare');
      if (p.hero.level < MASTERWORK.level) return fail('levelTooLow');
      if (p.hero.masterwork !== undefined) return fail('masterworkUsed');
      if ((p.hero.embers ?? 0) < MASTERWORK.embers) return fail('notEnoughEmbers');
      if ((p.hero.shards ?? 0) < MASTERWORK.shards) return fail('notEnoughShards');
      if (s.storage.dust < MASTERWORK.dust) return fail('notEnough');
      s.storage.dust -= MASTERWORK.dust;
      p.hero.embers = (p.hero.embers ?? 0) - MASTERWORK.embers;
      p.hero.shards = (p.hero.shards ?? 0) - MASTERWORK.shards;
      for (const k of Object.keys(item.attrs).sort() as GearAttr[]) item.attrs[k] = rollRange(item, item.rarity, k)[1];
      item.masterwork = true;
      p.hero.masterwork = item.id;
      event(s, now, 'masterwork', { pid, data: { item: item.id, slot: item.slot, dust: MASTERWORK.dust } });
      return OK;
    }
    case 'socket': {
      // A setting opened in a piece (docs/EQUIPO.md §5): each one more costs more and asks for a level.
      if (!forgeMaxPlus(s)) return fail('needsForge');
      const item = findItem(p, c.item);
      if (!item) return fail('noItem');
      const have = itemSockets(item);
      if (have.length >= socketMax(item)) return fail('maxSockets');
      const cost = SOCKET_OPEN[have.length];
      if (!cost) return fail('maxSockets');
      if (p.hero.level < cost.level) return fail('levelTooLow');
      if ((p.hero.embers ?? 0) < cost.embers) return fail('notEnoughEmbers');
      if ((p.hero.shards ?? 0) < cost.shards) return fail('notEnoughShards');
      if (s.storage.dust < cost.dust) return fail('notEnough');
      if (cost.embers) p.hero.embers = (p.hero.embers ?? 0) - cost.embers;
      if (cost.shards) p.hero.shards = (p.hero.shards ?? 0) - cost.shards;
      s.storage.dust -= cost.dust;
      item.sockets = [...have, null];
      delete item.socket;
      event(s, now, 'socket', { pid, data: { item: item.id, slot: item.slot, dust: cost.dust, embers: cost.embers, shards: cost.shards, n: item.sockets.length } });
      syncSlot(p.hero, item);
      return OK;
    }
    case 'setGem': {
      // A gem from the hero's pouch into a setting; the one there goes back to the pouch.
      if (!GEMS.includes(c.gem) || !GEM_CUTS.includes(c.cut)) return fail('badGem');
      const item = findItem(p, c.item);
      if (!item) return fail('noItem');
      const have = itemSockets(item);
      if (!Number.isInteger(c.at) || c.at < 0 || c.at >= have.length) return fail('noSocket');
      const was = have[c.at];
      if (was && was.gem === c.gem && was.cut === c.cut) return fail('sameGem');
      if ((p.hero.gems?.[gemKey(c.gem, c.cut)] ?? 0) < 1) return fail('noGem');
      pouch(p.hero, c.gem, c.cut, -1);
      if (was) pouch(p.hero, was.gem, was.cut, 1);
      item.sockets = have.map((g, i) => (i === c.at ? { gem: c.gem, cut: c.cut } : g));
      delete item.socket;
      event(s, now, 'gem', { pid, data: { item: item.id, slot: item.slot, gem: c.gem, cut: c.cut } });
      syncSlot(p.hero, item);
      return OK;
    }
    case 'unsetGem': {
      // A gem taken out of its setting goes back to the pouch whole.
      const item = findItem(p, c.item);
      if (!item) return fail('noItem');
      const have = itemSockets(item);
      const was = Number.isInteger(c.at) ? have[c.at] : undefined;
      if (!was) return fail('noGem');
      pouch(p.hero, was.gem, was.cut, 1);
      item.sockets = have.map((g, i) => (i === c.at ? null : g));
      delete item.socket;
      syncSlot(p.hero, item);
      return OK;
    }
    case 'cutGem': {
      // Crystal from the storage cut into a chipped or a whole gem, into the hero's pouch.
      if (!forgeMaxPlus(s)) return fail('needsForge');
      const cost = GEM_CUT_COST[c.cut];
      if (!GEMS.includes(c.gem) || !cost) return fail('badGem');
      if (p.hero.level < (GEM_CUT_LEVEL[c.cut] as number)) return fail('levelTooLow');
      if (s.storage.crystal < cost.crystal || s.storage.dust < cost.dust) return fail('notEnough');
      s.storage.crystal -= cost.crystal;
      s.storage.dust -= cost.dust;
      pouch(p.hero, c.gem, c.cut, 1);
      event(s, now, 'gemCut', { pid, data: { gem: c.gem, cut: c.cut, crystal: cost.crystal, dust: cost.dust } });
      return OK;
    }
    case 'fuseGems': {
      // Three gems of a cut fused into one of the next, with dust and (high cuts) embers, shards or a deep Tide's seal.
      if (!forgeMaxPlus(s)) return fail('needsForge');
      const cost = GEM_FUSE[c.cut];
      if (!GEMS.includes(c.gem) || !cost) return fail('badGem');
      if (p.hero.level < (GEM_CUT_LEVEL[c.cut] as number)) return fail('levelTooLow');
      const from = (c.cut - 1) as GemCut;
      if ((p.hero.gems?.[gemKey(c.gem, from)] ?? 0) < 3) return fail('noGem');
      if ((p.hero.embers ?? 0) < cost.embers) return fail('notEnoughEmbers');
      if ((p.hero.shards ?? 0) < cost.shards) return fail('notEnoughShards');
      if ((p.hero.seals?.tide ?? 0) < cost.tideSeal) return fail('noSeal');
      if (s.storage.dust < cost.dust) return fail('notEnough');
      s.storage.dust -= cost.dust;
      if (cost.embers) p.hero.embers = (p.hero.embers ?? 0) - cost.embers;
      if (cost.shards) p.hero.shards = (p.hero.shards ?? 0) - cost.shards;
      if (cost.tideSeal) p.hero.seals = { ...p.hero.seals, tide: (p.hero.seals?.tide ?? 0) - cost.tideSeal };
      pouch(p.hero, c.gem, from, -3);
      pouch(p.hero, c.gem, c.cut, 1);
      event(s, now, 'gemFuse', { pid, data: { gem: c.gem, cut: c.cut, dust: cost.dust } });
      return OK;
    }
    case 'awaken': {
      // Shards, embers and dust wake a relic the hero has (carried or kept): it stays awakened.
      if (!forgeMaxPlus(s)) return fail('needsForge');
      const h = p.hero;
      if (!h.relics.includes(c.relic) && !(h.relicBag ?? []).includes(c.relic)) return fail('noRelic');
      if ((h.awakened ?? []).includes(c.relic)) return fail('awake');
      if ((h.shards ?? 0) < RELIC_AWAKEN.shards) return fail('notEnoughShards');
      if ((h.embers ?? 0) < RELIC_AWAKEN.embers) return fail('notEnoughEmbers');
      if (s.storage.dust < RELIC_AWAKEN.dust) return fail('notEnough');
      h.shards = (h.shards ?? 0) - RELIC_AWAKEN.shards;
      h.embers = (h.embers ?? 0) - RELIC_AWAKEN.embers;
      s.storage.dust -= RELIC_AWAKEN.dust;
      h.awakened = [...(h.awakened ?? []), c.relic];
      event(s, now, 'awaken', { pid, data: { relic: c.relic } });
      return OK;
    }
    case 'offering': {
      // Dream dust burned at the keep: a blessing for every wave left in this Marea.
      if (s.status !== 'active') return fail('notActive');
      if (!OFFERING_KINDS.includes(c.kind)) return fail('badOffering');
      const have = offeringsFor(s, s.mareaIndex);
      if (have.includes(c.kind)) return fail('offered');
      const dust = offeringCost(s);
      if (s.storage.dust < dust) return fail('notEnough');
      s.storage.dust -= dust;
      s.offerings = { marea: s.mareaIndex, kinds: [...have, c.kind] };
      event(s, now, 'offering', { pid, data: { kind: c.kind, dust } });
      return OK;
    }
    case 'barter': {
      // The market of Musgoviejo (docs/SABERES.md §4.1): once a day, three of one for one of another (two if Odna trusts).
      if (!s.map.land || !standing(s, 'market')) return fail('needsMarket');
      if (s.barter && s.barter.day === day) return fail('barteredToday');
      const kinds = ['wood', 'stone', 'crystal'];
      if (!kinds.includes(c.give) || !kinds.includes(c.get) || c.give === c.get) return fail('badAmount');
      if (!Number.isInteger(c.n) || c.n < 1 || c.n > MARKET.maxPerDay) return fail('badAmount');
      const rate = (s.saga?.aff.odna ?? 0) >= MARKET.trustFrom ? MARKET.trustedRate : MARKET.rate;
      const gave = c.n * rate;
      if ((s.storage[c.give] ?? 0) < gave) return fail('notEnough');
      s.storage[c.give] -= gave;
      s.storage[c.get] += c.n;
      s.barter = { day, by: pid, give: c.give, get: c.get, gave, got: c.n };
      event(s, now, 'barter', { pid, data: { give: c.give, get: c.get, gave, got: c.n } });
      return OK;
    }
    case 'barterUndo': {
      // Only who made it, the same day, while what came in is still in the storehouse.
      const b = s.barter;
      if (!b || b.day !== day || b.got === 0) return fail('nothingToUndo');
      if (b.by !== pid) return fail('notYours');
      if ((s.storage[b.get] ?? 0) < b.got) return fail('spent');
      s.storage[b.get] -= b.got;
      s.storage[b.give] += b.gave;
      event(s, now, 'barterUndo', { pid, data: { give: b.give, get: b.get, gave: b.gave, got: b.got } });
      // The day's trade stays taken: undoing gives it back, not a second one.
      s.barter = { ...b, gave: 0, got: 0 };
      return OK;
    }
    case 'rite': {
      // A rite at the keep for the coming Marea (docs/SABERES.md §4.3): one, in the calm before it, a saber known.
      if (!s.map.land || !RITE_IDS.includes(c.id)) return fail('badRite');
      if (saberLocked(s, c.id)) return fail('locked');
      if (!riteOpen(s)) return fail('riteClosed');
      if (riteFor(s, s.mareaIndex)) return fail('riteChosen');
      // The alarm's bet is no bet with a boss coming.
      if (c.id === 'alarm' && Array.from({ length: WAVES_PER_MAREA }, (_, w) => bossAt(s.mareaIndex, w)).some(Boolean)) return fail('alarmBoss');
      const dust = offeringCost(s);
      if (s.storage.dust < dust) return fail('notEnough');
      s.storage.dust -= dust;
      s.rite = { marea: s.mareaIndex, repeat: s.mareaRepeats ?? 0, id: c.id, by: pid, dust };
      redraftForRite(s);
      event(s, now, 'rite', { pid, data: { id: c.id, dust, marea: s.mareaIndex } });
      return OK;
    }
    case 'riteUndo': {
      const id = riteFor(s, s.mareaIndex);
      if (!id || !s.rite) return fail('nothingToUndo');
      if (s.rite.by !== pid) return fail('notYours');
      if (!riteOpen(s)) return fail('riteClosed');
      const dust = s.rite.dust;
      s.storage.dust += dust;
      delete s.rite;
      redraftForRite(s);
      event(s, now, 'riteUndo', { pid, data: { id, dust } });
      return OK;
    }
    case 'recipe': {
      // A forge recipe's piece (docs/SABERES.md §4.4): its saber known, a forge standing, once a Marea, for this hero's class.
      const base = GEAR_BASES[c.base];
      if (!base?.recipe) return fail('badRecipe');
      if (saberLocked(s, RECIPE_SABER[c.base])) return fail('locked');
      if (!standing(s, 'forge')) return fail('needsForge');
      if (!canWearBase(cls, c.base)) return fail('cantWear');
      const slot = calendarMarea(s);
      if (p.hero.recipeMarea === slot) return fail('recipeMade');
      if (p.hero.bag.length >= MAX_BAG) return fail('bagFull');
      const dust = recipeDust(s);
      if (s.storage.dust < dust) return fail('notEnough');
      s.storage.dust -= dust;
      const item = recipePiece(s, p, c.base);
      p.hero.recipeMarea = slot;
      giveItem(s, p, item, now);
      event(s, now, 'recipe', { pid, data: { base: c.base, item: item.id, dust } });
      return OK;
    }
    case 'ensign': {
      // The group's ensign (docs/SABERES.md §4.5): a field, a colour and a sign; changed once an era.
      if (!knowsSaber(s, 'ensign')) return fail('locked');
      const ok = (n: number, max: number): boolean => Number.isInteger(n) && n >= 0 && n < max;
      if (!ok(c.field, ENSIGN.fields) || !ok(c.color, ENSIGN.colors) || !ok(c.sign, ENSIGN.signs)) return fail('badEnsign');
      if (!s.saberes) return fail('locked');
      if (s.saberes.ensign && s.saberes.ensign.era === eraOf(s)) return fail('ensignSet');
      s.saberes.ensign = { field: c.field, color: c.color, sign: c.sign, era: eraOf(s), by: pid };
      event(s, now, 'ensign', { pid, data: { field: c.field, color: c.color, sign: c.sign } });
      return OK;
    }
    case 'catName': {
      // The garrison's cat (docs/SABERES.md §4.5): named once, by the first of who brought it.
      if (!s.saberes?.known.some((k) => k.id === 'cat')) return fail('noCat');
      if (catNamer(s) !== pid) return fail('notYours');
      if (s.saberes.catName !== undefined) return fail('named');
      if (!Number.isInteger(c.name) || c.name < 0 || c.name >= CAT.names) return fail('badName');
      s.saberes.catName = c.name;
      event(s, now, 'catName', { pid, data: { name: c.name } });
      return OK;
    }
    case 'relicSwap': {
      // A kept relic in, a carried one out to the reserve.
      const bag = p.hero.relicBag ?? [];
      const i = p.hero.relics.indexOf(c.drop);
      if (!bag.includes(c.relic) || i < 0) return fail('noRelic');
      p.hero.relics[i] = c.relic;
      p.hero.relicBag = [...bag.filter((r) => r !== c.relic), c.drop];
      return OK;
    }
    case 'salvage': {
      const forge = Object.values(s.cells).some((cell) => cell.built?.structure === 'forge' && cell.built.hp > 0);
      if (!forge) return fail('needsForge');
      const i = p.hero.bag.findIndex((x) => x.id === c.item);
      if (i < 0) return fail('noItem');
      const item = p.hero.bag[i] as ItemInstance;
      // A unique piece is bound to its hero: it can't be melted.
      if (item.unique) return fail('bound');
      let plusCost = 0;
      for (let k = 0; k < item.plus; k++) plusCost += FORGE.dustPerLevel * (k + 1);
      const dust = SALVAGE_DUST[item.rarity] + mulBp(plusCost, SALVAGE_PLUS_BP);
      p.hero.bag.splice(i, 1);
      s.storage.dust += dust;
      event(s, now, 'salvage', { pid, data: { slot: item.slot, rarity: item.rarity, dust } });
      return OK;
    }
    case 'gift': {
      const to = s.players.find((x) => x.id === c.to);
      if (!to || to.left || to.id === pid) return fail('noPlayer');
      const i = p.hero.bag.findIndex((x) => x.id === c.item);
      if (i < 0) return fail('noItem');
      const item = p.hero.bag[i] as ItemInstance;
      // A unique piece, or one made from a forge recipe, is bound to its hero.
      if (item.unique || item.bound) return fail('bound');
      if (to.hero.bag.length >= MAX_BAG) return fail('bagFull');
      p.hero.bag.splice(i, 1);
      item.from = pid;
      to.hero.bag.push(item);
      event(s, now, 'gift', { pid, data: { to: to.id, slot: item.slot, rarity: item.rarity, item: item.id } });
      return OK;
    }
    case 'fulfill': {
      const task = s.board.find((x) => x.id === c.task);
      if (!task || task.done) return fail('noTask');
      if (!isNonNegRes(c.res)) return fail('badAmount');
      const left = res({ wood: task.want.wood - task.got.wood, stone: task.want.stone - task.got.stone, crystal: task.want.crystal - task.got.crystal, dust: task.want.dust - task.got.dust });
      const give = res({
        wood: imin(c.res.wood ?? 0, left.wood),
        stone: imin(c.res.stone ?? 0, left.stone),
        crystal: imin(c.res.crystal ?? 0, left.crystal),
        dust: imin(c.res.dust ?? 0, left.dust),
      });
      if (sumRes(give) <= 0) return fail('nothingToGive');
      if (!hasRes(s.storage, give)) return fail('notEnough');
      addRes(s.storage, give, -1);
      addRes(task.got, give);
      creditGiven(s, pid, give);
      // Resources for a blueprint go straight into it.
      const j = task.cellKey ? s.cells[task.cellKey]?.job : null;
      if (j && j.stage === 'blueprint') {
        const need = remaining(j);
        const into = res({ wood: imin(need.wood, give.wood), stone: imin(need.stone, give.stone), crystal: imin(need.crystal, give.crystal) });
        addRes(j.paid, into);
        addRes(s.storage, scaleRes(res({ wood: give.wood - into.wood, stone: give.stone - into.stone, crystal: give.crystal - into.crystal }), 10000));
        if (sumRes(remaining(j)) === 0) {
          j.stage = 'building';
          j.fundedAt = now;
        }
      }
      if (task.got.wood >= task.want.wood && task.got.stone >= task.want.stone && task.got.crystal >= task.want.crystal && task.got.dust >= task.want.dust) {
        task.done = true;
        if (task.by !== pid) grantXp(s, pid, XP.task, now);
        event(s, now, 'task', { pid, data: { done: true, by: task.by } });
      }
      return OK;
    }
    case 'task': {
      if (!isNonNegRes(c.want) || sumRes(c.want) === 0) return fail('badAmount');
      s.board.push({
        id: s.nextId++,
        by: pid,
        at: now,
        cellKey: c.q === null || c.r === null ? null : hexKey({ q: c.q, r: c.r }),
        want: res(c.want),
        got: res({}),
        done: false,
        text: cleanText(c.text, 120),
      });
      if (s.board.length > 40) s.board.splice(0, s.board.length - 40);
      event(s, now, 'task', { pid });
      return OK;
    }
    default:
      return fail('unsupported');
  }
};

/** Level of the best standing barracks (0: none). */
export const barracksLevel = (s: CampaignState): number => {
  let best = 0;
  for (const cell of Object.values(s.cells)) {
    const b = cell.built;
    if (b?.structure === 'barracks' && b.hp > 0 && b.level > best) best = b.level;
  }
  return best;
};

/** How many mercenaries can serve at once (by the barracks level). */
export const mercenarySlots = (s: CampaignState): number => {
  const lv = barracksLevel(s);
  return lv > 0 ? (MERCENARY.slots[lv - 1] as number) : 0;
};

/** Share of their class's strength mercenaries fight with; a fallen barracks still pays level 1. */
export const mercenaryPowerBp = (s: CampaignState): number => MERCENARY.powerBp[imax(1, barracksLevel(s)) - 1] as number;

/** Mercenaries hired this Marea, in hiring order. */
export const servingMercenaries = (s: CampaignState): NonNullable<CampaignState['mercs']> => (s.mercs ?? []).filter((m) => m.marea >= s.mareaIndex);

/** 80 wood + 40 stone × (4 / active players, min 0.5) — SPEC §9. */
export const mercenaryCost = (s: CampaignState): Resources => {
  const n = s.players.filter((p) => !p.left).length;
  let bp = n > 0 ? Math.trunc(40000 / n) : 40000;
  if (bp < MERCENARY.minScaleBp) bp = MERCENARY.minScaleBp;
  return scaleRes(MERCENARY.cost, bp);
};

export const contributeAll = (s: CampaignState, pid: string, q: number, r: number, now: Ms): CommandResult => {
  const j = s.cells[hexKey({ q, r })]?.job;
  if (!j) return fail('noBlueprint');
  const left = remaining(j);
  const give = res({
    wood: left.wood < s.storage.wood ? left.wood : s.storage.wood,
    stone: left.stone < s.storage.stone ? left.stone : s.storage.stone,
    crystal: left.crystal < s.storage.crystal ? left.crystal : s.storage.crystal,
    iron: left.iron < (s.storage.iron ?? 0) ? left.iron : s.storage.iron ?? 0,
  });
  return applyCampaignCommand(s, pid, { t: 'contribute', q, r, res: give }, now);
};

export { remaining as jobRemaining };
